import {
    CELL_BORDER_EDGES,
    HTML_EDITOR_TABLE_DEFAULT_COLS,
    HTML_EDITOR_TABLE_DEFAULT_ROWS,
    HTML_EDITOR_TABLE_MAX_COLS,
    HTML_EDITOR_TABLE_MAX_ROWS,
    HTML_EDITOR_TABLE_MIN_COLS,
    HTML_EDITOR_TABLE_MIN_ROWS,
    type VisualEditorCellEdge
} from '../editor-types';
import { closestFromNode, getNearestTable } from './selection';

const CELL_BORDER = '1px solid #cccccc';
const CELL_PADDING = '8px';
const CELL_STYLE = `border: ${CELL_BORDER}; padding: ${CELL_PADDING};`;

const clampTableSize = (value: number, min: number, max: number): number => {
    if (!Number.isFinite(value)) return min;
    return Math.min(max, Math.max(min, Math.round(value)));
};

const rangeNode = (range: Range | null): Node | null => range?.startContainer ?? null;

export const getNearestCell = (node: Node | null): HTMLTableCellElement | null => {
    const cell = closestFromNode(node, 'td, th');
    const tag = cell?.tagName;
    // Prefer tagName: iframe nodes fail `instanceof HTMLTableCellElement` (different realm).
    return tag === 'TD' || tag === 'TH' ? (cell as HTMLTableCellElement) : null;
};

export const getNearestRow = (node: Node | null): HTMLTableRowElement | null => {
    const row = closestFromNode(node, 'tr');
    return row?.tagName === 'TR' ? (row as HTMLTableRowElement) : null;
};

/** Data-like tables have at least one row with 2+ cells (skips single-cell email layout wrappers). */
export const isDataLikeTable = (table: HTMLTableElement | null): boolean => {
    if (!table) return false;
    return Array.from(table.rows).some((row) => row.cells.length >= 2);
};

export const getDataLikeRow = (node: Node | null): HTMLTableRowElement | null => {
    const row = getNearestRow(node);
    if (!row) return null;
    return isDataLikeTable(getNearestTable(row)) ? row : null;
};

export const placeCaretInCell = (document: Document, cell: HTMLTableCellElement | null): void => {
    if (!cell) return;

    const range = document.createRange();
    range.selectNodeContents(cell);
    range.collapse(true);

    const selection = document.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
};

const cloneEmptyCell = (cell: HTMLTableCellElement): HTMLTableCellElement => {
    const clone = cell.cloneNode(false) as HTMLTableCellElement;
    clone.removeAttribute('colspan');
    clone.removeAttribute('rowspan');
    clone.replaceChildren(cell.ownerDocument.createTextNode('\u00a0'));
    return clone;
};

export const createEmailTable = (document: Document, rows = HTML_EDITOR_TABLE_DEFAULT_ROWS, cols = HTML_EDITOR_TABLE_DEFAULT_COLS): HTMLTableElement => {
    rows = clampTableSize(rows, HTML_EDITOR_TABLE_MIN_ROWS, HTML_EDITOR_TABLE_MAX_ROWS);
    cols = clampTableSize(cols, HTML_EDITOR_TABLE_MIN_COLS, HTML_EDITOR_TABLE_MAX_COLS);
    const table = document.createElement('table');
    table.setAttribute('border', '1');
    table.setAttribute('cellpadding', '8');
    table.setAttribute('cellspacing', '0');
    table.setAttribute('width', '100%');
    table.setAttribute('style', 'border-collapse: collapse; width: 100%;');

    const tbody = document.createElement('tbody');
    for (let rowIndex = 0; rowIndex < rows; rowIndex += 1) {
        const row = document.createElement('tr');
        for (let colIndex = 0; colIndex < cols; colIndex += 1) {
            const cell = document.createElement('td');
            cell.setAttribute('style', CELL_STYLE);
            cell.textContent = '\u00a0';
            row.appendChild(cell);
        }
        tbody.appendChild(row);
    }

    table.appendChild(tbody);
    return table;
};

export const insertTable = (document: Document, range: Range | null, rows = HTML_EDITOR_TABLE_DEFAULT_ROWS, cols = HTML_EDITOR_TABLE_DEFAULT_COLS): void => {
    const table = createEmailTable(document, rows, cols);
    const start = rangeNode(range);
    const block = closestFromNode(start, 'p, h1, h2, h3, h4, h5, h6, pre, blockquote');

    if (block?.parentNode) {
        block.parentNode.insertBefore(table, block.nextSibling);
    } else if (range) {
        range.deleteContents();
        range.insertNode(table);
    } else {
        document.body?.appendChild(table);
    }

    placeCaretInCell(document, table.rows[0]?.cells[0] ?? null);
};

export const addTableRow = (document: Document, range: Range | null, position: 'above' | 'below'): void => {
    const row = getNearestRow(rangeNode(range));
    if (!row?.parentNode) return;

    const clone = row.cloneNode(false) as HTMLTableRowElement;
    Array.from(row.cells).forEach((cell) => clone.appendChild(cloneEmptyCell(cell)));

    if (position === 'above') {
        row.parentNode.insertBefore(clone, row);
    } else {
        row.parentNode.insertBefore(clone, row.nextSibling);
    }

    placeCaretInCell(document, clone.cells[0] ?? null);
};

export const deleteTableRow = (document: Document, range: Range | null): void => {
    const row = getNearestRow(rangeNode(range));
    const table = getNearestTable(rangeNode(range));
    if (!row || !table) return;

    const rowIndex = row.rowIndex;
    row.remove();

    if (table.rows.length === 0) {
        table.remove();
        return;
    }

    const focusRow = table.rows[Math.min(Math.max(rowIndex, 0), table.rows.length - 1)];
    placeCaretInCell(document, focusRow?.cells[0] ?? null);
};

export const addTableColumn = (document: Document, range: Range | null, position: 'before' | 'after', columnIndex?: number): void => {
    const table = getNearestTable(rangeNode(range));
    if (!table) return;

    const cell = getNearestCell(rangeNode(range));
    const index = columnIndex ?? cell?.cellIndex;
    if (index === undefined || index < 0) return;

    const sourceRow = cell?.parentElement?.tagName === 'TR' ? (cell.parentElement as HTMLTableRowElement) : table.rows[0];
    let focusCell: HTMLTableCellElement | null = null;

    Array.from(table.rows).forEach((row) => {
        const reference = row.cells[index] ?? row.cells[row.cells.length - 1];
        if (!reference) {
            const created = document.createElement('td');
            created.textContent = '\u00a0';
            row.appendChild(created);
            if (row === sourceRow) focusCell = created;
            return;
        }

        const clone = cloneEmptyCell(reference);
        if (position === 'before') {
            row.insertBefore(clone, reference);
        } else {
            row.insertBefore(clone, reference.nextSibling);
        }
        if (row === sourceRow) focusCell = clone;
    });

    placeCaretInCell(document, focusCell);
};

export const deleteTableColumn = (document: Document, range: Range | null, columnIndex?: number): void => {
    const table = getNearestTable(rangeNode(range));
    if (!table) return;

    const cell = getNearestCell(rangeNode(range));
    const index = columnIndex ?? cell?.cellIndex;
    if (index === undefined || index < 0) return;
    const row = cell?.parentElement?.tagName === 'TR' ? (cell.parentElement as HTMLTableRowElement) : table.rows[0];
    const nextIndex = Math.max(0, index - 1);

    Array.from(table.rows).forEach((tableRow) => {
        tableRow.cells[index]?.remove();
    });

    if (Array.from(table.rows).every((tableRow) => tableRow.cells.length === 0)) {
        table.remove();
        return;
    }

    const focusRow = row?.isConnected ? row : table.rows[0];
    const focusIndex = Math.min(nextIndex, Math.max((focusRow?.cells.length ?? 1) - 1, 0));
    placeCaretInCell(document, focusRow?.cells[focusIndex] ?? null);
};

export const deleteTable = (_document: Document, range: Range | null): void => {
    getNearestTable(rangeNode(range))?.remove();
};

const flattenTableCells = (table: HTMLTableElement): HTMLTableCellElement[] => Array.from(table.rows).flatMap((row) => Array.from(row.cells));

export type CellNavigationResult = 'moved' | 'add-row' | 'noop';
export type VisualTabResult = CellNavigationResult | 'stay';

export const handleVisualEditorTab = (
    document: Document,
    event: { preventDefault(): void; stopPropagation(): void; shiftKey: boolean },
    range: Range | null
): VisualTabResult => {
    event.preventDefault();
    event.stopPropagation();
    if (!getNearestCell(rangeNode(range))) return 'stay';
    return focusAdjacentCell(document, range, event.shiftKey ? 'previous' : 'next');
};

export const focusAdjacentCell = (document: Document, range: Range | null, direction: 'next' | 'previous'): CellNavigationResult => {
    const table = getNearestTable(rangeNode(range));
    const cell = getNearestCell(rangeNode(range));
    if (!table || !cell) return 'noop';

    const cells = flattenTableCells(table);
    const index = cells.indexOf(cell);
    if (index < 0) return 'noop';

    const nextIndex = direction === 'next' ? index + 1 : index - 1;
    if (nextIndex < 0) return 'noop';
    if (nextIndex >= cells.length) return direction === 'next' ? 'add-row' : 'noop';

    placeCaretInCell(document, cells[nextIndex]);
    return 'moved';
};

export const insertCellLineBreak = (document: Document, range: Range | null): void => {
    if (!getNearestCell(rangeNode(range))) return;

    const selection = document.getSelection();
    const activeRange = range ?? (selection?.rangeCount ? selection.getRangeAt(0) : null);
    if (!activeRange) return;

    activeRange.deleteContents();
    const lineBreak = document.createElement('br');
    activeRange.insertNode(lineBreak);

    const nextRange = document.createRange();
    nextRange.setStartAfter(lineBreak);
    nextRange.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(nextRange);
};

const LIGHT_ROW_BORDER = '1px solid #eee';

const CELL_BORDER_STYLE_PROP: Record<VisualEditorCellEdge, 'borderTop' | 'borderRight' | 'borderBottom' | 'borderLeft'> = {
    top: 'borderTop',
    right: 'borderRight',
    bottom: 'borderBottom',
    left: 'borderLeft'
};

const cellPosition = (table: HTMLTableElement, cell: HTMLTableCellElement): { row: number; col: number } | null => {
    for (let row = 0; row < table.rows.length; row += 1) {
        const col = Array.from(table.rows[row].cells).indexOf(cell);
        if (col >= 0) return { row, col };
    }
    return null;
};

export const getCellsInRange = (_document: Document, range: Range | null): HTMLTableCellElement[] => {
    const startCell = getNearestCell(rangeNode(range));
    if (!startCell) return [];
    if (!range) return [startCell];

    const endCell = getNearestCell(range.endContainer) ?? startCell;
    const table = getNearestTable(startCell);
    if (!table || getNearestTable(endCell) !== table || startCell === endCell) return [startCell];

    const startPos = cellPosition(table, startCell);
    const endPos = cellPosition(table, endCell);
    if (!startPos || !endPos) return [startCell];

    const minRow = Math.min(startPos.row, endPos.row);
    const maxRow = Math.max(startPos.row, endPos.row);
    const minCol = Math.min(startPos.col, endPos.col);
    const maxCol = Math.max(startPos.col, endPos.col);
    const cells: HTMLTableCellElement[] = [];
    for (let row = minRow; row <= maxRow; row += 1) {
        for (let col = minCol; col <= maxCol; col += 1) {
            const cell = table.rows[row]?.cells[col];
            if (cell) cells.push(cell);
        }
    }
    return cells.length > 0 ? cells : [startCell];
};

const expandCellBorderShorthand = (cell: HTMLTableCellElement): void => {
    const shorthand = cell.style.border;
    if (!shorthand) return;
    const sides: Record<VisualEditorCellEdge, string> = {
        top: cell.style.borderTop || shorthand,
        right: cell.style.borderRight || shorthand,
        bottom: cell.style.borderBottom || shorthand,
        left: cell.style.borderLeft || shorthand
    };
    cell.style.border = '';
    CELL_BORDER_EDGES.forEach((edge) => {
        cell.style[CELL_BORDER_STYLE_PROP[edge]] = sides[edge];
    });
};

export const applyCellBorders = (document: Document, range: Range | null, edges: VisualEditorCellEdge[], color: string): void => {
    if (edges.length === 0) return;
    const borderValue = color.trim() ? `1px solid ${color.trim()}` : '';
    getCellsInRange(document, range).forEach((cell) => {
        expandCellBorderShorthand(cell);
        edges.forEach((edge) => {
            cell.style[CELL_BORDER_STYLE_PROP[edge]] = borderValue;
        });
    });
};

export const applyCellFill = (document: Document, range: Range | null, color: string): void => {
    const fill = color.trim();
    getCellsInRange(document, range).forEach((cell) => {
        cell.style.backgroundColor = fill;
        if (!fill) cell.style.background = '';
    });
};

export const applyRowBorderBottom = (_document: Document, range: Range | null, border: 'none' | 'light'): void => {
    const row = getNearestRow(rangeNode(range));
    if (!row) return;

    Array.from(row.cells).forEach((cell) => {
        if (border === 'light') {
            cell.style.borderBottom = LIGHT_ROW_BORDER;
            return;
        }

        const remaining = (cell.getAttribute('style') ?? '')
            .split(';')
            .map((declaration) => declaration.trim())
            .filter((declaration) => {
                const name = declaration.split(':')[0]?.trim().toLowerCase();
                return Boolean(name) && !name.startsWith('border-bottom');
            })
            .join('; ');

        if (remaining) {
            cell.setAttribute('style', remaining);
        } else {
            cell.removeAttribute('style');
        }
    });
};
